import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Smartphone, Lock, AlertCircle, ArrowLeft, Send } from 'react-feather';
import 'react-toastify/dist/ReactToastify.css';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://192.168.1.13:8000/api/';

type FormData = {
  mobile: string;
  otp?: string;
};

export default function DeleteAccount() {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [countryCode, setCountryCode] = useState('+91');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const onRequestOtp = async (data: FormData) => {
    setLoading(true);
    try {
      const fullMobile = `${countryCode}${data.mobile}`;
      const response = await axios.post(`${baseUrl}request-deletion-otp`, { mobile: fullMobile });
      toast.success(response.data.message || 'OTP sent successfully!');
      setStep(2);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to send OTP. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmDeletion = async (data: FormData) => {
    setLoading(true);
    try {
      const fullMobile = `${countryCode}${data.mobile}`;
      await axios.post(`${baseUrl}confirm-deletion`, {
        mobile: fullMobile,
        otp: data.otp,
      });
      toast.success(
        '✅ Account deleted successfully. Please log out from the mobile app to complete the process.',
        { autoClose: 6000 }
      );
      reset();
      setStep(1);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to delete account. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Icon & Title */}
        <div style={styles.iconWrap}>
          <AlertCircle size={44} color="#dc2626" />
        </div>
        <h2 style={styles.title}>Delete Account</h2>
        <p style={styles.subtitle}>
          {step === 1
            ? "We're sorry to see you go. Enter your mobile number to receive a verification code."
            : 'Enter the 5-digit code sent to your WhatsApp to confirm deletion.'}
        </p>

        <form onSubmit={handleSubmit(step === 1 ? onRequestOtp : onConfirmDeletion)} style={styles.form}>

          {/* Mobile Number */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="mobile">Mobile Number</label>
            <div style={styles.mobileRow}>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                disabled={step === 2}
                style={styles.select}
              >
                <option value="+91">🇮🇳 +91</option>
                <option value="+966">🇸🇦 +966</option>
                <option value="+971">🇦🇪 +971</option>
                <option value="+1">🇺🇸 +1</option>
                <option value="+44">🇬🇧 +44</option>
              </select>
              <div style={styles.inputWrap}>
                <Smartphone size={18} style={styles.inputIcon} color="#9ca3af" />
                <Controller
                  name="mobile"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Mobile number is required',
                    pattern: { value: /^[0-9]{7,15}$/, message: 'Enter a valid mobile number' },
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="mobile"
                      type="text"
                      disabled={step === 2}
                      placeholder="9876543210"
                      style={{
                        ...styles.input,
                        borderColor: errors.mobile ? '#fca5a5' : '#e5e7eb',
                        background: step === 2 ? '#f9fafb' : '#fff',
                      }}
                    />
                  )}
                />
              </div>
            </div>
            {errors.mobile && <p style={styles.error}>{errors.mobile.message}</p>}
          </div>

          {/* OTP Field (step 2 only) */}
          {step === 2 && (
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="otp">Verification Code</label>
              <div style={styles.inputWrap}>
                <Lock size={18} style={styles.inputIcon} color="#9ca3af" />
                <Controller
                  name="otp"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'OTP is required',
                    minLength: { value: 5, message: 'OTP must be 5 digits' },
                    maxLength: { value: 5, message: 'OTP must be 5 digits' },
                    pattern: { value: /^[0-9]{5}$/, message: 'Enter a valid 5-digit OTP' },
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="otp"
                      type="text"
                      placeholder="Enter 5-digit OTP"
                      maxLength={5}
                      style={{
                        ...styles.input,
                        borderColor: errors.otp ? '#fca5a5' : '#e5e7eb',
                      }}
                    />
                  )}
                />
              </div>
              {errors.otp && <p style={styles.error}>{errors.otp.message}</p>}
            </div>
          )}

          {/* Submit Button */}
          <button type="submit" disabled={loading} style={styles.submitBtn}>
            {loading ? (
              <span style={styles.loadingRow}>
                <span style={styles.spinner} /> Processing...
              </span>
            ) : step === 1 ? (
              <span style={styles.btnContent}>
                <Send size={16} /> &nbsp; Send Verification Code
              </span>
            ) : (
              '🗑️  Confirm & Delete Account'
            )}
          </button>

          {/* Bottom links */}
          <div style={styles.linksRow}>
            <Link to="/privacy-policy" style={styles.backLink}>
              <ArrowLeft size={14} /> &nbsp; Privacy Policy
            </Link>
            {step === 2 && (
              <button
                type="button"
                style={styles.changeBtn}
                onClick={() => { setStep(1); reset({ otp: '' }); }}
              >
                Change Number
              </button>
            )}
          </div>
        </form>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fdf2f2 0%, #fef3f2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 16px',
    fontFamily: "'Segoe UI', Roboto, sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: 440,
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 20px 60px rgba(220,38,38,0.08)',
    padding: '48px 40px',
    textAlign: 'center',
    border: '1px solid #fecaca',
  },
  iconWrap: {
    width: 80,
    height: 80,
    background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 4px 16px rgba(220,38,38,0.15)',
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    color: '#111827',
    margin: '0 0 10px',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: 14,
    lineHeight: 1.7,
    margin: '0 0 32px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    textAlign: 'left',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
  },
  mobileRow: {
    display: 'flex',
    gap: 8,
  },
  select: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '10px 8px',
    fontSize: 14,
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
    flexShrink: 0,
  },
  inputWrap: {
    position: 'relative',
    flex: 1,
  },
  inputIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    padding: '10px 12px 10px 38px',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    fontSize: 14,
    color: '#111827',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  error: {
    color: '#dc2626',
    fontSize: 12,
    margin: 0,
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(220,38,38,0.3)',
    transition: 'opacity 0.2s',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  spinner: {
    width: 16,
    height: 16,
    border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'spin 0.8s linear infinite',
  },
  btnContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linksRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    color: '#6b7280',
    fontSize: 13,
    textDecoration: 'none',
    fontWeight: 500,
    gap: 4,
  },
  changeBtn: {
    background: 'none',
    border: 'none',
    color: '#dc2626',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
  },
};
